import React, { useEffect, useState } from 'react';
import { InitialStep } from './InitialStep';
import { FinalStep } from './FinalStep';
import { FeedbackStep } from './FeedbackStep';
import { CancelationReason, INVOICE_TYPE_ISSUED, PauseSubscriptionOption } from '../../types';
import { BenefitsStep } from './BenefitsStep';
import { useTimelineData } from '../../hooks/useTimelineData';
import { BoxSpinner } from '../../../commons/components/Spinner';
import { PauseStep } from './PauseStep';
import { useGradualRollout } from '../../../commons/hooks/useGradualRollout';
import { billingPortalConfig } from '../../../commons/config';
import useApi from '../../../api/backendApiContext';

type WizardSteps = 'preparation' | 'benefits' | 'initial' | 'pause' | 'feedback' | 'final';

export const CancellationWizard = () => {
  const [step, setStep] = useState<WizardSteps | null>('preparation');
  const [hasBenefitsStep, setHasBenefitsStep] = useState(false);
  const [pauseOptions, setPauseOptions] = useState<PauseSubscriptionOption[] | null>(null);
  const { billingApi } = useApi();

  const [isInitialized, setIsInitialized] = useState(false);
  const timelineData = useTimelineData();
  const { isEnabled: pauseFeatureIsEnabled } = useGradualRollout(
    'pause-cancellation-step',
    billingPortalConfig.pauseOnCancellationRolloutLevel
  );
  useEffect(() => {
    if (!pauseFeatureIsEnabled) {
      setPauseOptions([]);
    } else {
      // noinspection JSIgnoredPromiseFromCall
      billingApi
        .getPauseSubscriptionOptions()
        .then((x) => setPauseOptions(x))
        .catch(() => setPauseOptions([]));
    }
  }, [billingApi, pauseFeatureIsEnabled]);
  useEffect(() => {
    if (isInitialized) return;
    if (step === 'preparation' && timelineData.type === 'data' && pauseOptions != null) {
      setIsInitialized(true);

      const issuedInvoices = timelineData.data.invoicesAndBenefits.filter(
        (x) => typeof x.invoice === 'object' && x.invoice?.type === INVOICE_TYPE_ISSUED
      );
      if (timelineData.data.billingPlan.name === 'month' && issuedInvoices.length > 2) {
        setHasBenefitsStep(true);
        setStep('benefits');
      } else {
        setHasBenefitsStep(false);
        setStep('initial');
      }
    }
  }, [timelineData, step, pauseOptions, isInitialized, pauseFeatureIsEnabled]);

  const [selectedReasons, setSelectedReasons] = useState<CancelationReason[]>([]);
  const [comment, setComment] = useState('');
  const [closed, setClosed] = useState(false);
  if (closed) return null;

  return (
    <>
      {step === 'preparation' && (timelineData.type === 'component' || !pauseOptions) && (
        <BoxSpinner />
      )}
      {step === 'benefits' && (
        <BenefitsStep onNext={() => setStep('initial')} onCancel={() => setStep(null)} />
      )}
      {step === 'initial' && (
        <InitialStep
          hasPreviousStep={hasBenefitsStep}
          onNext={() => setStep((pauseOptions?.length ?? 0) > 0 ? 'pause' : 'feedback')}
          onGoBackClicked={() => setStep(hasBenefitsStep ? 'benefits' : null)}
        />
      )}
      {step === 'pause' && pauseOptions && pauseOptions.length > 0 && (
        <PauseStep
          pauseOptions={pauseOptions}
          onNext={() => setStep('feedback')}
          onGoBackClicked={() => setStep('initial')}
          onClosed={() => setClosed(true)}
        />
      )}
      {step === 'feedback' && (
        <FeedbackStep
          selectedReasons={selectedReasons}
          onSelectedReasonsChanged={setSelectedReasons}
          comment={comment}
          onCommentChanged={setComment}
          onGoBackClicked={() => setStep((pauseOptions?.length ?? 0) > 0 ? 'pause' : 'initial')}
          onBillingPlanCanceled={() => setStep('final')}
        />
      )}
      {step === 'final' && <FinalStep />}
    </>
  );
};
